import {
  html,
  type ReactiveController,
  type ReactiveControllerHost,
  type TemplateResult,
} from "lit";
import { cn } from "@/utilities/css";
import { CSAT_RATINGS, type CsatRating, type FeedbackSurveySubmitDetail } from "./types";

interface CsatFeedbackCallbacks {
  onSkip: () => void;
  onSubmit: (detail: FeedbackSurveySubmitDetail) => void;
}

export class CsatFeedbackController implements ReactiveController {
  private _csatFeedback = "";
  private _csatRating: number | undefined = undefined;

  private _onFeedbackInput = (e: CustomEvent<{ value: string }>) => {
    // No requestUpdate: nothing rendered depends on the feedback text (the
    // textarea holds its own value), so re-rendering on every keystroke is waste.
    if (typeof e.detail?.value === "string") {
      this._csatFeedback = e.detail.value;
    }
  };

  private _onSubmit = () => {
    if (!this._csatRating) return;
    this.callbacks.onSubmit({
      feedback: this._csatFeedback,
      feedbackType: "csat",
      rating: this._csatRating,
    });
  };

  constructor(
    private host: ReactiveControllerHost & HTMLElement,
    private callbacks: CsatFeedbackCallbacks
  ) {
    host.addController(this);
  }

  private _onRatingKeydown(e: KeyboardEvent, value: number) {
    const ratings = Array.from(
      this.host.shadowRoot!.querySelectorAll<HTMLElement>(".eko-feedback-survey__csat-rating")
    );
    const index = CSAT_RATINGS.findIndex((r) => r.value === value);

    switch (e.key) {
      case "ArrowDown":
      case "ArrowRight": {
        e.preventDefault();
        const nextIndex = (index + 1) % ratings.length;
        this._csatRating = CSAT_RATINGS[nextIndex].value;
        this.host.requestUpdate();
        this.host.updateComplete.then(() => ratings[nextIndex].focus());
        break;
      }
      case "ArrowUp":
      case "ArrowLeft": {
        e.preventDefault();
        const prevIndex = (index - 1 + ratings.length) % ratings.length;
        this._csatRating = CSAT_RATINGS[prevIndex].value;
        this.host.requestUpdate();
        this.host.updateComplete.then(() => ratings[prevIndex].focus());
        break;
      }
      case " ":
      case "Enter":
        e.preventDefault();
        this._csatRating = value;
        this.host.requestUpdate();
        break;
    }
  }

  private _renderRating(rating: CsatRating): TemplateResult {
    const selected = this._csatRating === rating.value;
    const tabindex = selected || (!this._csatRating && rating.value === 1) ? 0 : -1;
    return html`
      <div
        class=${cn("eko-feedback-survey__csat-rating", {
          "eko-feedback-survey__csat-rating--selected": selected,
        })}
        role="radio"
        aria-checked=${String(selected)}
        aria-label=${rating.label}
        tabindex=${tabindex}
        @click=${() => {
          this._csatRating = rating.value;
          this.host.requestUpdate();
        }}
        @keydown=${(e: KeyboardEvent) => this._onRatingKeydown(e, rating.value)}>
        <span class="eko-feedback-survey__csat-emoji" aria-hidden="true">${rating.emoji}</span>
        <span class="eko-feedback-survey__csat-rating-label" aria-hidden="true">
          ${rating.label}
        </span>
      </div>
    `;
  }

  private _resolvePlaceholder(
    negative: string | undefined,
    neutral: string | undefined,
    positive: string | undefined
  ): string {
    if (this._csatRating !== undefined) {
      if (this._csatRating <= 2 && negative) return negative;
      if (this._csatRating === 3 && neutral) return neutral;
      if (this._csatRating >= 4 && positive) return positive;
    }
    return "Provide feedback (optional)";
  }

  // Required: ReactiveController's hooks are all optional, so a controller that
  // implements none of them fails TypeScript's weak-type check at addController.
  hostConnected() {}

  render(params: {
    followUpNegative: string | undefined;
    followUpNeutral: string | undefined;
    followUpPositive: string | undefined;
    loading: boolean;
    question: string;
  }): TemplateResult {
    const { followUpNegative, followUpNeutral, followUpPositive, loading, question } = params;

    const placeholder = this._resolvePlaceholder(
      followUpNegative,
      followUpNeutral,
      followUpPositive
    );

    return html`
      <div class="eko-feedback-survey__body">
        <div class="eko-feedback-survey__csat-ratings" role="radiogroup" aria-label=${question}>
          ${CSAT_RATINGS.map((r) => this._renderRating(r))}
        </div>
        <eko-textarea
          placeholder=${placeholder}
          .value=${this._csatFeedback}
          .rows=${3}
          @input=${this._onFeedbackInput}></eko-textarea>
      </div>

      <div class="eko-feedback-survey__footer">
        <eko-button-group variant="equal">
          <eko-button ?disabled=${loading} @click=${this.callbacks.onSkip}> Skip </eko-button>
          <eko-button
            variant="primary"
            ?disabled=${!this._csatRating || loading}
            ?loading=${loading}
            @click=${this._onSubmit}>
            Send
          </eko-button>
        </eko-button-group>
      </div>
    `;
  }

  reset() {
    this._csatRating = undefined;
    this._csatFeedback = "";
  }
}
